import React, { PropsWithChildren, ReactNode } from 'react';
import clsx from 'clsx';
import {
  Colors,
  ColorsType,
  FontSize,
  FontWeight,
  LetterSpacing,
  LetterSpacingTokenType,
  LineHeight,
} from '@common/tokens';

type Props = PropsWithChildren<{
  id?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  weight?: 'light' | 'medium' | 'bold' | 'bolder';
  color?: ColorsType;
  className?: string;
  transform?: 'capitalize' | 'lowercase' | 'uppercase';
  align?: 'left' | 'center' | 'right';
  italic?: boolean;
  deleted?: boolean;
  secondary?: boolean;
  bold?: boolean; // alias for weight="bold"
  block?: boolean;
  underline?: boolean;
  info?: string;
  dataTestId?: string;
  letterSpacing?: LetterSpacingTokenType;
}>;

export function Text(props: Props) {
  const {
    id,
    size = 'md',
    weight = 'light',
    color = 'textDefault',
    transform,
    align,
    className,
    children,
    italic,
    deleted,
    secondary,
    bold,
    block,
    underline,
    info,
    dataTestId,
    letterSpacing,
  } = props;

  const finalColor = secondary ? 'midnightLighter' : color;
  const colorHash = Colors[finalColor];
  const finalWeight = bold ? 'bold' : weight;
  const internalClassName = clsx(
    className,
    `wk-text wk-text-size--${size} wk-text-weight--${finalWeight}`,
    transform && `wk-text-transform--${transform}`,
    align && `wk-text-align--${align}`,
    letterSpacing && `wk-text-letter-spacing--${letterSpacing}`,
    {
      'wk-text--italic': italic,
      'wk-text--deleted': deleted,
      'wk-text--underline': underline,
      'wk-text--block': block || !!align,
    }
  );

  return (
    <>
      <span
        id={id}
        title={info}
        data-testid={dataTestId}
        className={internalClassName}
      >
        {applyPropInheritanceInTextChildren(children, props)}
      </span>
      <style jsx>{`
        .wk-text {
          color: ${colorHash};
          margin: 0;
        }

        .wk-text--italic {
          font-style: italic;
        }

        .wk-text-weight--light {
          font-weight: ${FontWeight.light};
        }
        .wk-text-weight--medium {
          font-weight: ${FontWeight.medium};
        }
        .wk-text-weight--bold {
          font-weight: ${FontWeight.bold};
        }
        .wk-text-weight--bolder {
          font-weight: 800;
        }

        span.wk-text-size--sm {
          font-size: ${FontSize.xs};
          line-height: ${LineHeight.xs};
        }
        span.wk-text-size--md {
          font-size: ${FontSize.sm};
          line-height: ${LineHeight.sm};
        }
        span.wk-text-size--lg {
          font-size: ${FontSize.md};
          line-height: ${LineHeight.md};
        }
        span.wk-text-size--xl {
          font-size: ${FontSize.xl};
          line-height: ${LineHeight.lg};
        }

        .wk-text-transform--capitalize {
          text-transform: capitalize;
        }
        .wk-text-transform--lowercase {
          text-transform: lowercase;
        }
        .wk-text-transform--uppercase {
          text-transform: uppercase;
        }

        .wk-text--block {
          display: block;
        }

        .wk-text-align--left {
          text-align: left;
        }
        .wk-text-align--center {
          text-align: center;
        }
        .wk-text-align--right {
          text-align: right;
        }

        .wk-text--deleted {
          text-decoration: line-through;
        }

        .wk-text--underline {
          text-decoration: underline;
          text-decoration-color: ${colorHash};
        }

        .wk-text-letter-spacing--xs {
          letter-spacing: ${LetterSpacing.xs};
        }
        .wk-text-letter-spacing--sm {
          letter-spacing: ${LetterSpacing.sm};
        }
        .wk-text-letter-spacing--md {
          letter-spacing: ${LetterSpacing.md};
        }
        .wk-text-letter-spacing--lg {
          letter-spacing: ${LetterSpacing.lg};
        }
      `}</style>
    </>
  );
}

function applyPropInheritanceInTextChildren(
  children: ReactNode,
  parentTextProps: Props
): ReactNode {
  return React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === Text) {
      const {
        children: _,
        dataTestId: __,
        className: ___,
        id: ____,
        ...parentInheritable
      } = parentTextProps;

      return React.cloneElement(child, {
        ...parentInheritable,
        ...child.props,
      });
    }
    return child;
  });
}
